// Text view: stored page text is one line per printed line, with no paragraphs or sizes. This works out the
// shape of the page from what's stored (browser only, nothing downloaded): page furniture, section labels,
// headlines, standfirsts, bylines, interview questions and paragraphs. No words are removed.
// Blocks: { t: 'meta' | 'label' | 'head' | 'deck' | 'by' | 'sub' | 'q' | 'p', text }

const END = /[.!?]["”’')]*$/;
const MONTH = /\b(jan(uary)?|feb(ruary)?|mar(ch)?|apr(il)?|may|june?|july?|aug(ust)?|sep(t(ember)?)?|oct(ober)?|nov(ember)?|dec(ember)?)\b/i;
const DAY = /\b(mon|tues|wednes|thurs|fri|satur|sun)day\b/i;
const SMALL = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'the', 'to', 'with', 'over', 'after', 'into', 'is', 'are', 'not', 'but']);

const key = (s) => String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
const wordsOf = (s) => s.split(/\s+/).filter(Boolean);
const lettersOf = (s) => s.replace(/[^\p{L}]/gu, '');
const startsLower = (s) => /^\p{Ll}/u.test(s || '');
const isCaps = (s) => { const l = lettersOf(s); return l.length >= 2 && l === l.toUpperCase(); };
const median = (a) => { const b = [...a].sort((x, y) => x - y); return b.length ? b[Math.floor(b.length / 2)] : 0; };

// "T H I S D AY" and similar: mostly one- or two-letter pieces.
const isSpaced = (s) => { const w = wordsOf(s); return w.length >= 4 && w.filter((x) => lettersOf(x).length <= 2).length / w.length >= 0.6; };

// Most longer words start with a capital, as in "Hamzat Unveils 100-point Agenda, Pledges".
const isTitled = (s) => {
  const big = wordsOf(s).filter((w) => lettersOf(w).length && !SMALL.has(lettersOf(w).toLowerCase()));
  if (big.length < 2) return false;
  return big.filter((w) => /^["“‘'(]?\p{Lu}/u.test(w) || /^\p{N}/u.test(w)).length / big.length >= 0.8;
};

const isNameLike = (s) => {
  const w = wordsOf(s);
  if (!w.length || w.length > (/\b(and|in|from)\b|&/i.test(s) ? 7 : 4) || /\d|[,.:;!?]$/.test(s) && !/\.$/.test(s)) return false;
  return w.every((x) => /^\p{Lu}[\p{L}'’.-]*,?$/u.test(x) || ['and', 'in', 'of', 'from', 'with', 'by'].includes(x.toLowerCase()));
};

const endsOpen = (s) => SMALL.has(lettersOf(wordsOf(s).pop() || '').toLowerCase());
const isDateLine = (s) => s.length <= 44 && MONTH.test(s) && /\b(19|20)\d{2}\b/.test(s) && (DAY.test(s) || /^\W*\d/.test(s)) && !END.test(s) && !/[,;:]$/.test(s);

const isMeta = (s, i, n) => {
  if (/^(page\s+\d+|continued (on|from) page|cont(’|')?d on|www\.|https?:|\||e-?mail\s*:|tel\s*:|phone\s*:|sms\s*:|like us on |follow us on )/i.test(s)) return true;
  if (/^[\w ]{0,20}editor\s*:/i.test(s) || isDateLine(s)) return true;
  if (s.length <= 60 && /\b0\d{10}\b/.test(s.replace(/[\s-]/g, ' ')) && !END.test(s)) return true; // a reporter's name and phone number
  if (/^vol(\.|ume)?\s*\d/i.test(s)) return true;
  if (i >= 5 && i < n - 3) return false;
  if (/^\d{1,3}$/.test(s) || isSpaced(s)) return true;
  return s.length <= 70 && MONTH.test(s) && /\b(19|20)\d{2}\b/.test(s) && (DAY.test(s) || /\d/.test(s));
};

// Some PDFs (mostly adverts) store a coded font instead of letters. Two codes can be undone here:
// every letter shifted by 29 ("*RYHUQRU" = "Governor"), and bytes swapped ("愀渀搀" = "and").
const CONTROL = /[\x00-\x08\x0b\x0c\x0e-\x1f]/; // eslint-disable-line no-control-regex
const COMMON = /\b(the|and|of|to|in|that|for|was|is|with|on|at)\b/gi;
const unshift = (s) => s.replace(/[\x03-\x5d]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 29));
const isSwapped = (code) => code >= 0x2000 && (code & 0xff) === 0;
const swapBytes = (s) => s.replace(/[\u0100-\uffff]/g, (c) => { const v = c.charCodeAt(0); return String.fromCharCode(((v & 0xff) << 8) | (v >> 8)); });

function looksShifted(chunk) {
  if (!chunk || /[a-cA-C]|[d-z]/.test(chunk) || !/[\x03-\x5d]/.test(chunk)) return false; // coded text has no a-z, and A, B, C would decode to symbols
  const out = unshift(chunk);
  const plain = (out.match(/[A-Za-z0-9]/g) || []).length;
  if (plain < out.replace(/\s/g, '').length / 2) return false;
  // Ordinary capitals turn into nonsense with few vowels ("TUESDAY" -> "qrbpa^v"), so real words must have vowels.
  const letters = out.match(/[A-Za-z]/g) || [];
  if (letters.length && letters.filter((c) => /[aeiou]/i.test(c)).length < letters.length * 0.25) return false;
  if (CONTROL.test(chunk)) return true;
  return chunk.length >= 3 && letters.filter((c) => /[a-z]/.test(c)).length >= out.length * 0.6;
}

function mendCodes(body) {
  // The "fi" and "fl" joined letters in Sun papers arrive swapped even on otherwise clean lines ("昀椀nding").
  const lines = String(body || '').replace(/\r/g, '').replace(/昀椀/g, 'fi').replace(/昀氀/g, 'fl').split('\n').map((line) => {
    const wide = [...line].filter((c) => c.charCodeAt(0) >= 0x100);
    const swapped = wide.filter((c) => isSwapped(c.charCodeAt(0))).length;
    return swapped >= 3 && swapped >= wide.length / 2 ? swapBytes(line) : line;
  });
  const coded = lines.map((l) => l.split(' ').filter(looksShifted).map(unshift).join(' ')).join(' ');
  const common = (coded.match(COMMON) || []).length;
  if (common < 3 || common < wordsOf(coded).length * 0.1) return lines.join('\n');
  return lines.map((l) => l.split(' ').map((c) => (looksShifted(c) ? unshift(c) : c)).join(' ').replace(/¶/g, '’')).join('\n');
}

// Control characters that are left carry no words: drop them, and any line that was only symbols.
function dropControl(text) {
  return text.replace(/[\x7f-\x9f\ue000-\uf8ff\ufffd]/g, ' ').split('\n').map((l) => (CONTROL.test(l) ? l.replace(new RegExp(CONTROL.source, 'g'), ' ') : l))
    .filter((l, i, all) => /[\p{L}\p{N}]/u.test(l) || !CONTROL.test(String(all[i]))).join('\n');
}

const prepare = (body) => dropControl(mendCodes(body));

// True when a good part of the page is a code we can't undo, so the reader is better off on the printed page.
export function hardToRead(body) {
  const mended = mendCodes(body);
  const left = (mended.match(new RegExp(CONTROL.source, 'g')) || []).length;
  const letters = (mended.match(/\p{L}/gu) || []).length;
  const odd = (mended.match(/[\u0100-\u024f\u0370-\u03ff\u0300-\u036f]/g) || []).length;
  return left > 20 && left > letters / 3 ? true : letters > 40 && odd > letters / 4;
}

// A capital left alone on its line is a drop cap: "Y" + "ou transitioned".
function mendDropCaps(lines) {
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const l = lines[i];
    const next = lines[i + 1];
    if (/^\p{Lu}$/u.test(l) && startsLower(next)) {
      out.push(`${l}${/^[AI]$/.test(l) ? ' ' : ''}${next}`);
      i += 1;
    } else out.push(l);
  }
  return out;
}

// Lines belonging to the headlines found by type size when the edition was processed (these are reliable).
function markKnownHeads(lines, headline) {
  const marks = new Array(lines.length).fill(false);
  const known = String(headline || '').split('\n').map(key).filter((k) => k.length >= 8);
  const found = new Set();
  for (let i = 0; i < lines.length; i += 1) {
    let joined = '';
    for (let j = i; j < Math.min(i + 6, lines.length); j += 1) {
      joined += key(lines[j]);
      const hit = known.find((k) => k === joined);
      if (hit) {
        for (let m = i; m <= j; m += 1) marks[m] = true;
        found.add(hit);
        break;
      }
    }
  }
  return { marks, unmatched: known.length - found.size };
}

export function layoutPage(body, headline) {
  const lines = mendDropCaps(prepare(body).split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean));
  const { marks } = markKnownHeads(lines, headline);
  const n = lines.length;
  const blocks = [];
  let para = [];
  let lastKind = null;
  const flush = () => {
    if (!para.length) return;
    const text = para.join(' ').replace(/(\p{L})- (\p{Ll})/gu, '$1$2').replace(/\s{2,}/g, ' ').trim();
    para = [];
    if (text.length > 1) { blocks.push({ t: 'p', text }); lastKind = 'p'; }
  };
  const push = (t, text) => {
    flush();
    const prev = blocks[blocks.length - 1];
    if (prev && prev.t === t && t !== 'p' && t !== 'q') prev.text += t === 'meta' ? ` · ${text}` : ` ${text}`;
    else blocks.push({ t, text });
    lastKind = t;
  };
  const width = median(lines.filter((l) => l.length > 20).map((l) => l.length)) || 40;

  for (let i = 0; i < n; i += 1) {
    const l = lines[i];
    const prevEnded = !para.length || END.test(para[para.length - 1]);
    const next = lines[i + 1];
    if (marks[i]) { push('head', l); continue; }
    if (isMeta(l, i, n)) {
      // Page furniture landing mid-sentence must not cut the paragraph in two.
      const tidy = i < 5 ? l.replace(/\b\p{Lu}{1,2}(?: \p{Lu}{1,2}){2,}\b/gu, (m) => m.replace(/ /g, '')) : l; // "T H I S D AY"
      if (para.length && !END.test(para[para.length - 1])) blocks.push({ t: 'meta', text: tidy });
      else push('meta', tidy);
      continue;
    }
    if (prevEnded && l.length <= 3 && isCaps(l) && lastKind !== 'p') { push('meta', l); continue; }

    // A headline ending on "over", "of", "to" and so on carries on to this line.
    if (lastKind === 'head' && !para.length && endsOpen(blocks[blocks.length - 1].text) && l.length <= 80 && !END.test(l)) { push('head', l); continue; }

    if (prevEnded && !startsLower(l)) {
      // Standfirst or byline straight under a headline.
      if (lastKind === 'by' && !END.test(blocks[blocks.length - 1].text) && /[,&]$|\(.*\).*&/.test(blocks[blocks.length - 1].text + l) && l.length <= 70 && /\(|&/.test(l)) { push('by', l); continue; }
      if (lastKind === 'head' && /^from\s+\p{Lu}/iu.test(l) && l.length <= 70 && !END.test(l)) { push('by', l); continue; }
      if (lastKind === 'head' || lastKind === 'deck') {
        if (isNameLike(l) && !END.test(l)) { push('by', l); continue; }
        if (lastKind === 'head' && !/[.,;:]$/.test(l) && l.length <= 90 && next && !startsLower(next) && wordsOf(l).length >= 3 && !endsOpen(l) && (isNameLike(next) || (isTitled(next) && !next.includes(',')) || l.length > width * 1.2)) { push('deck', l); continue; }
      }
      if (lastKind === 'by' && isNameLike(l) && wordsOf(l).length <= 2 && !END.test(l) && next && !startsLower(next)) { push('by', l); continue; }
      if (/^by\s+\p{Lu}/iu.test(l) && l.length <= 60 && !END.test(l) && !startsLower(next)) { push('by', l); continue; }

      const caps = isCaps(l);
      if (caps && wordsOf(l).length <= 4 && l.length <= 34 && !/[.,;:]$/.test(l)) { push('label', l); continue; }
      // Unmarked headline: titled or capitals, ends openly, and the next line doesn't carry on mid-sentence.
      const words = wordsOf(l).length;
      const stillHead = lastKind === 'head';
      // A titled line as wide as the column is more likely a sentence that happens to hold names ("Speaking in Calabar, Cross River").
      const columnWide = l.length >= width * 0.8 && l.length <= width * 1.25;
      // A comma in the next line means a sentence running on ("Planning, Senator Abubakar Atiku"), not a second headline line.
      const nextTitled = next && (isTitled(next) || isCaps(next)) && !next.includes(',');
      if ((caps || isTitled(l)) && !/[.,;]$/.test(l) && !/\p{Ll}\. \p{Lu}/u.test(l) && (caps || !/\b(is|was|are|were) (the|a|an)\b/.test(l)) && l.length <= 80 && (words >= 4 || (stillHead && words >= 1)) && !startsLower(next) && (!endsOpen(l) || nextTitled) && (stillHead || caps || nextTitled || !columnWide)) { push('head', l); continue; }
      // A short line with no full stop, between finished sentences: a subheading ("Building a cyber strong culture").
      const sentenceCase = !isTitled(l) && !isCaps(l);
      const endsOnName = /^\p{Lu}/u.test(wordsOf(l).pop() || '');
      if (lastKind === 'sub' && words <= 4 && !/[.,;:!?]$/.test(l) && next && !startsLower(next) && isTitled(l)) { push('sub', l); continue; }
      if (words >= 2 && words <= 9 && !(sentenceCase && endsOnName) && l.length < width * 0.85 && !/[.,;:!]$/.test(l) && !endsOpen(l) && next && !startsLower(next) && !/^\S+,/.test(next) && lastKind === 'p') { push('sub', l); continue; }
    }
    // A wrapped headline's next line can start small or follow a comma.
    if (lastKind === 'head' && !para.length && l.length <= 80 && !END.test(l) && isTitled(l) && !startsLower(next)) { push('head', l); continue; }

    para.push(l);
    lastKind = 'p';
    const joinedLength = para.join(' ').length;
    if (END.test(l) && (l.length < width * 0.82 || joinedLength > 900)) flush();
  }
  flush();

  // Interview pages: questions stand alone. Only when a page has at least two, so a lone rhetorical line stays normal.
  const asks = blocks.filter((b) => b.t === 'p' && /\?["”’']?$/.test(b.text) && b.text.length <= 240);
  if (asks.length >= 2) asks.forEach((b) => { b.t = 'q'; });
  return blocks;
}

// A stored headline that isn't in the page text (rare) is still shown at the top, unless its words are stuck together.
export function looseHeadline(body, headline) {
  const text = prepare(body);
  const { unmatched } = markKnownHeads(mendDropCaps(text.split('\n').map((l) => l.trim()).filter(Boolean)), headline);
  if (!unmatched) return null;
  const whole = key(text);
  const left = String(headline || '').split('\n').map((h) => h.trim()).filter((h) => h.length >= 8 && !/\p{Ll}\p{Lu}/u.test(h) && !whole.includes(key(h)));
  return left.length ? left.join(' · ') : null;
}
